import type { Sides } from "@project/emerge-types";
import { Alignment } from "../enum/Alignment";
import { OrientationOnRect } from "../enum/OrientationOnRect";
import type { ConnectorInfos } from "../interfaces/ConnectorInfos";
import type { Position } from "../interfaces/Position";
import { getOpposingSide } from "../user_options/Side";
import { RectPropertiesBySide } from "./RectProperties";

/**
 * Whether an axis points the other way on the opposing face.
 *
 * Every face defines its axes as seen from outside the case, so an axis can
 * either reverse across a pair of faces (FRONT/BACK horizontal: +x vs -x) or
 * keep pointing the same way (FRONT/BACK vertical: +y on both). Placement
 * parameters are stored per face, so the same stored value lands on opposite
 * physical ends exactly when the axis reverses.
 */
export function axisDirsOpposite(
  side: Sides,
  orientation: OrientationOnRect,
): boolean {
  const own = RectPropertiesBySide[side];
  const other = RectPropertiesBySide[getOpposingSide(side)];
  if (!own || !other) return false;

  const key =
    orientation === OrientationOnRect.HORIZONTAL
      ? "horizontalDir"
      : "verticalDir";
  return own[key].dot(other[key]) < 0;
}

/**
 * Whether a placement on the opposing face has to flip along the given axis.
 *
 * Mirroring (the default) means reflecting the arrangement, so it flips exactly
 * the axes that reverse across the face pair. Point symmetry is a 180° turn
 * instead, which is the complement: it flips exactly the axes that do not
 * reverse. Deciding per axis rather than per option is what keeps a mirrored
 * pair level — on a face pair whose vertical axis does not reverse, flipping
 * vertically would stagger the two parts diagonally.
 */
export function shouldFlipOnOppositeSide(
  side: Sides,
  orientation: OrientationOnRect,
  pointSymmetry: boolean | undefined,
): boolean {
  return axisDirsOpposite(side, orientation) !== (pointSymmetry === true);
}

/**
 * Mirror a resolved placement along its own axis.
 *
 * Equivalent to what `calcPosition` produces for a mirrored copy, but applied
 * after the fact — `Rect.resolvePosition1d` measures START and END from the
 * same end of the edge, so only `n` and a centred placement's signed margin
 * carry the flip.
 */
export function flipPosition(position: Position): Position {
  return {
    ...position,
    margin:
      position.alignment === Alignment.CENTER
        ? -position.margin
        : position.margin,
    n: 1 - position.n,
  };
}

/**
 * The placement a compiled connector slot takes on an edge of the given face
 * and orientation, and whether it was flipped to get there.
 *
 * Shared by the build (`EdgeInfos.addConnector`) and the geometry-only
 * margin probe, so both see the same effective `n` and flip.
 */
export function resolveConnectorPlacement(
  connector: Pick<
    ConnectorInfos,
    "position" | "onOppositeSide" | "pointSymmetry"
  >,
  side: Sides,
  orientation: OrientationOnRect,
): { position: Position; flipped: boolean } {
  const flipped =
    connector.onOppositeSide === true &&
    shouldFlipOnOppositeSide(side, orientation, connector.pointSymmetry);
  return {
    position: flipped ? flipPosition(connector.position) : connector.position,
    flipped,
  };
}
